/**
 * PHARMAKON — locale & formatters (Algerian French)
 *
 * Ported from the design's config/locale.config.js. ESM exports replace the
 * window-attached globals.
 */

import {
  formatDecimal,
  MINUS_SIGN,
  THOUSANDS_SEP,
  UNKNOWN_VALUE,
} from "../lib/number-format.js";

export const WILAYAS = [
  { code: "01", name: "Adrar" },
  { code: "02", name: "Chlef" },
  { code: "03", name: "Laghouat" },
  { code: "04", name: "Oum El Bouaghi" },
  { code: "05", name: "Batna" },
  { code: "06", name: "Béjaïa" },
  { code: "07", name: "Biskra" },
  { code: "08", name: "Béchar" },
  { code: "09", name: "Blida" },
  { code: "10", name: "Bouira" },
  { code: "11", name: "Tamanrasset" },
  { code: "12", name: "Tébessa" },
  { code: "13", name: "Tlemcen" },
  { code: "14", name: "Tiaret" },
  { code: "15", name: "Tizi Ouzou" },
  { code: "16", name: "Alger" },
  { code: "17", name: "Djelfa" },
  { code: "18", name: "Jijel" },
  { code: "19", name: "Sétif" },
  { code: "20", name: "Saïda" },
  { code: "21", name: "Skikda" },
  { code: "22", name: "Sidi Bel Abbès" },
  { code: "23", name: "Annaba" },
  { code: "24", name: "Guelma" },
  { code: "25", name: "Constantine" },
  { code: "26", name: "Médéa" },
  { code: "27", name: "Mostaganem" },
  { code: "28", name: "M'Sila" },
  { code: "29", name: "Mascara" },
  { code: "30", name: "Ouargla" },
  { code: "31", name: "Oran" },
  { code: "32", name: "El Bayadh" },
  { code: "33", name: "Illizi" },
  { code: "34", name: "Bordj Bou Arréridj" },
  { code: "35", name: "Boumerdès" },
  { code: "36", name: "El Tarf" },
  { code: "37", name: "Tindouf" },
  { code: "38", name: "Tissemsilt" },
  { code: "39", name: "El Oued" },
  { code: "40", name: "Khenchela" },
  { code: "41", name: "Souk Ahras" },
  { code: "42", name: "Tipaza" },
  { code: "43", name: "Mila" },
  { code: "44", name: "Aïn Defla" },
  { code: "45", name: "Naâma" },
  { code: "46", name: "Aïn Témouchent" },
  { code: "47", name: "Ghardaïa" },
  { code: "48", name: "Relizane" },
  { code: "49", name: "Timimoun" },
  { code: "50", name: "Bordj Badji Mokhtar" },
  { code: "51", name: "Ouled Djellal" },
  { code: "52", name: "Béni Abbès" },
  { code: "53", name: "In Salah" },
  { code: "54", name: "In Guezzam" },
  { code: "55", name: "Touggourt" },
  { code: "56", name: "Djanet" },
  { code: "57", name: "El M'Ghair" },
  { code: "58", name: "El Meniaa" },
];

export const LOCALE_LABELS = {
  // Statuts génériques
  BROUILLON: "Brouillon",
  VALIDE: "Validé",
  AVOIR: "Avoir",
  EN_COURS: "En cours",
  VALIDEE: "Validée",
  ANNULEE: "Annulée",
  CREEE: "Créée",
  ENVOYEE: "Envoyée",
  IMPORTEE: "Importée",
  PARTIELLEMENT_ACCEPTEE: "Partiellement acceptée",
  REJETEE: "Rejetée",
  PAYEE: "Payée",
  OUVERTE: "Ouverte",
  // Schema enum is CLOTUREE; keep the legacy FERMEE alias for any place that
  // still pulls the older label, but the canonical key is CLOTUREE.
  CLOTUREE: "Clôturée",
  FERMEE: "Fermée",
  EN_ATTENTE: "En attente",
  SUSPENDUE: "Suspendue",
  REVISION_REQUISE: "Révision requise",
  // Rôles
  PROPRIETAIRE: "Propriétaire",
  MANAGER: "Manager",
  CAISSIER: "Caissier",
  FOURNISSEUR: "Fournisseur",
  ADMIN_PHARMAKON: "Admin Pharmakon",
  LABORATOIRE: "Laboratoire",
  // Types vente
  LIBRE: "Libre",
  CHIFA: "CHIFA",
  SANS_CARTE: "Sans carte",
  // Types produit (full TypeProduit enum — append-only, mirrors the server)
  MEDICAMENT: "Médicament",
  PARAPHARMACIE: "Parapharmacie",
  COSMETIQUE: "Cosmétique",
  CONSOMMABLE: "Consommable",
  MATERIEL_MEDICAL: "Matériel médical",
  COMPLEMENT_ALIMENTAIRE: "Complément alimentaire",
  // X XWP-13 (AUD-PROD-23) — the seventh TypeProduit value was missing from every label table.
  ORTHOPEDIE: "Orthopédie",
  // TypeOrdonnance (Prisma enum) — the panel's « Psychotrope · Ordonnance … » chip.
  STANDARD: "Ordonnance standard",
  BLANC_1_SOUCHE: "Ordonnance blanche — 1 souche",
  BLANC_3_SOUCHES: "Ordonnance blanche — 3 souches",
  // Classifications
  LISTE_I: "Liste I",
  LISTE_II: "Liste II",
  HORS_LISTE: "Hors liste",
  TABLEAU_B: "Tableau B",
};

/**
 * THE money formatter (ux/01 §3.2, OP-F-04 — `formatCurrency` delegates here).
 * « 1 250,00 DA » with a narrow no-break space for thousands, a comma decimal,
 * a true minus sign (« −3 200,00 DA ») and « — » for an unknown value — never
 * « 0,00 » before a value exists. Accepts numbers, decimal strings (the server's
 * Prisma `Decimal`) and Decimal instances; rounding is exact (BigInt), never a
 * binary-float `toFixed`.
 *
 * @param {unknown} v
 * @param {{ noSuffix?: boolean, signed?: boolean, decimals?: number }} [opts]
 *   noSuffix — grid cells whose header carries « (DA) » (P-06);
 *   signed   — écarts, soldes, ajustements: « +73,42 DA ».
 */
export const fmtMoney = (v, opts = {}) => {
  const s = formatDecimal(v, {
    decimals: opts.decimals ?? 2,
    signed: Boolean(opts.signed),
  });
  if (s === UNKNOWN_VALUE) return s;
  return opts.noSuffix ? s : s + " DA";
};

export const fmtInt = (v) =>
  v == null
    ? "—"
    : Number(v)
        .toLocaleString("fr-DZ")
        .replace(/\s/g, THOUSANDS_SEP)
        .replace("-", MINUS_SIGN);

export const fmtPct = (v, decimals = 0) =>
  v == null ? "—" : Number(v).toFixed(decimals) + " %";

export const fmtDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  return dt.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

export const fmtDateLong = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  return dt.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export const fmtTime = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  return dt.toLocaleTimeString("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const fmtRelTime = (d) => {
  if (!d) return "—";
  const now = Date.now();
  const t = new Date(d).getTime();
  const diff = Math.round((t - now) / 1000);
  const abs = Math.abs(diff);
  if (abs < 60) return diff < 0 ? "à l'instant" : "dans un instant";
  if (abs < 3600)
    return diff < 0
      ? `il y a ${Math.round(abs / 60)} min`
      : `dans ${Math.round(abs / 60)} min`;
  if (abs < 86400)
    return diff < 0
      ? `il y a ${Math.round(abs / 3600)} h`
      : `dans ${Math.round(abs / 3600)} h`;
  return diff < 0
    ? `il y a ${Math.round(abs / 86400)} j`
    : `dans ${Math.round(abs / 86400)} j`;
};
